// ============================================================
// CLUBHUB – Navigation (eine Quelle fuer Leiste, Mobil, Suche)
// ============================================================
// Die acht ClubDesk-Module in clubhub-Zuschnitt, gruppiert wie im
// Agent (`NAV_GRUPPEN`). Wer einen Bereich ergaenzt, ergaenzt ihn
// HIER — Leiste, mobile Navigation und Befehlspalette lesen alle
// diese Liste. Rechte je Eintrag folgen mit dem Backend
// (`lib/permissions.ts`).
// ============================================================
import {
  BookOpenText,
  CalendarDays,
  CircleUserRound,
  ClipboardList,
  FolderOpen,
  Globe,
  HandHeart,
  LayoutDashboard,
  Mail,
  Megaphone,
  Receipt,
  Repeat,
  Settings,
  UserRound,
  Users,
  UsersRound,
  type LucideIcon,
} from "lucide-react";

export const NAV_GRUPPEN = ["Verein", "Finanzen", "Kommunikation"] as const;
export type NavGruppe = (typeof NAV_GRUPPEN)[number] | null;

export type NavEintrag = {
  href: string;
  label: string;
  icon: LucideIcon;
  gruppe: NavGruppe;
  /** Kurzer Satz fuer Tooltip und Befehlspalette. */
  hinweis: string;
  /** Unten in der Handy-Leiste? (hoechstens vier) */
  mobil?: boolean;
};

export const NAVIGATION: NavEintrag[] = [
  { href: "/", label: "Übersicht", icon: LayoutDashboard, gruppe: null, hinweis: "Was heute wichtig ist", mobil: true },

  { href: "/mitglieder", label: "Mitglieder", icon: Users, gruppe: "Verein", hinweis: "Mitglieder, Kontakte, Familien", mobil: true },
  { href: "/gruppen", label: "Gruppen", icon: UsersRound, gruppe: "Verein", hinweis: "Mannschaften, Abteilungen, Vorstand" },
  { href: "/kalender", label: "Kalender", icon: CalendarDays, gruppe: "Verein", hinweis: "Termine, Zu- und Absagen, Helfer", mobil: true },
  { href: "/pinnwand", label: "Pinnwand", icon: Megaphone, gruppe: "Verein", hinweis: "Aushänge und Aufgaben" },

  { href: "/beitraege", label: "Beiträge", icon: Repeat, gruppe: "Finanzen", hinweis: "Beitragsarten, Beitragsläufe, SEPA" },
  { href: "/rechnungen", label: "Rechnungen", icon: Receipt, gruppe: "Finanzen", hinweis: "Rechnungen, Zahlungen, Mahnungen" },
  { href: "/buchhaltung", label: "Buchhaltung", icon: BookOpenText, gruppe: "Finanzen", hinweis: "Buchungen, Konten, Kontoauszüge" },
  { href: "/spenden", label: "Spenden", icon: HandHeart, gruppe: "Finanzen", hinweis: "Spenden, Aufrufe, Zuwendungsbestätigungen" },

  { href: "/nachrichten", label: "Nachrichten", icon: Mail, gruppe: "Kommunikation", hinweis: "Serienmails, Newsletter, Vorlagen", mobil: true },
  { href: "/dokumente", label: "Dokumente", icon: FolderOpen, gruppe: "Kommunikation", hinweis: "Archiv mit Zugriffsrechten" },
  { href: "/website", label: "Website", icon: Globe, gruppe: "Kommunikation", hinweis: "Vereinsseite und Mitgliederbereich" },
  { href: "/formulare", label: "Formulare", icon: ClipboardList, gruppe: "Kommunikation", hinweis: "Aufnahmeanträge und Anmeldungen" },
];

/** Unten in der Leiste, getrennt von den Bereichen. */
export const NAV_FUSS: NavEintrag[] = [
  { href: "/mein-bereich", label: "Mein Bereich", icon: UserRound, gruppe: null, hinweis: "So sieht es ein Mitglied" },
  { href: "/einstellungen", label: "Einstellungen", icon: Settings, gruppe: null, hinweis: "Verein, Benutzer, Rechte, Schnittstellen" },
];

/**
 * Das eigene Konto: kein Menuepunkt — der Weg ist der eigene Name
 * unten in der Leiste. Steht hier fuer Kopfzeilen-Titel und Suche.
 */
export const NAV_KONTO: NavEintrag = { href: "/mein-konto", label: "Mein Konto", icon: CircleUserRound, gruppe: null, hinweis: "Profil, Benachrichtigungen, Anmeldung" };

export const ALLE_EINTRAEGE = [...NAVIGATION, ...NAV_FUSS, NAV_KONTO];

/** Ist `href` der aktive Bereich fuer `pathname`? */
export function istAktiv(pathname: string, href: string): boolean {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

/** Der Titel der Kopfzeile: der Bereich, in dem man steht. */
export function bereichFuer(pathname: string): NavEintrag | undefined {
  return ALLE_EINTRAEGE.find((e) => e.href !== "/" && istAktiv(pathname, e.href)) ??
    (pathname === "/" ? NAVIGATION[0] : undefined);
}
